import React, { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import debounce from './Utils/debounce';
import { getInitialLanguage } from './i18n';
import { stripLangPrefix } from './Utils/langPath';
import './stylesheet.css';
import Breadcrumbs from "./Component/Layout/Breadcrumbs/Breadcrumbs";
import HomeFigure from "./Pages/Home/HomeFigure/HomeFigure";
import ScrollToTop from "./Utils/ScrollToTop";
import ProductPage from "./Component/Pagination/productPage";
import KineticLoader from "./Component/UI/KineticLoader/KineticLoader";
import NewArrival from "./Pages/Home/NewArrival/NewArrival";
import TrendingCategories from "./Pages/Home/TrendingCategories/TrendingCategories";
import Wishlist from "./Pages/Wishlist/Wishlist";
import Unsubscribe from "./Pages/Unsubscribe/Unsubscribe";
import Subscribe from "./Pages/Subscribe/Subscribe";
import AuthRoutes from "./Pages/Auth/AuthRoutes";
import CartCheckoutRoutes from "./Pages/CartCheckout/CartCheckoutRoute";
import OrderRoutes from "./Pages/Orders/OrderRoutes";
import SupportChat from "./Component/SupportChat/SupportChat";
import { dualStoreRoute } from './routes/dualStoreRoute';
import StoreScopedRedirect from './routes/StoreScopedRedirect';
import PageViewBeacon from './Component/storefront/PageViewBeacon';
import StoreHead from './Component/storefront/StoreHead';
import { usePublicSettings } from './Context/PublicSettingsContext';

function isPageEnabled(slug, publicSettings) {
    if (!publicSettings?.storefrontPages || typeof publicSettings.storefrontPages !== 'object') {
        return true;
    }
    const entry = publicSettings.storefrontPages[slug];
    if (!entry || typeof entry !== 'object') return true;
    return entry.enabled !== false;
}

// The store resolve call behind "Loading store…" takes long enough for every chunk to arrive,
// so start them all at startup. Lazy-loading on first render instead flashes the Suspense
// spinner for a moment between "Loading store…" and the page. The chunks are small next to main.js.
function preloadedLazy(factory) {
    let loaded = null;
    const promise = factory().then((module) => {
        loaded = module.default;
        return module;
    });
    // A failed chunk still rejects inside lazy() when rendered; don't also report it at startup.
    promise.catch(() => {});
    const LazyComponent = lazy(() => promise);
    // Pick the render path once so a component mounted through lazy() is never remounted later.
    let Component = null;
    return function PreloadedComponent(props) {
        if (!Component) Component = loaded || LazyComponent;
        return <Component {...props} />;
    };
}

const Header = preloadedLazy(() => import('./Component/Layout/Header/Header'));
const Footer = preloadedLazy(() => import('./Component/Layout/Footer/Footer'));
const Product = preloadedLazy(() => import('./Pages/Product/product'));
const ProductDetail = preloadedLazy(() => import('./Pages/ProductDetails/productDetail'));
const About = preloadedLazy(() => import('./Pages/About/About'));
const Contact = preloadedLazy(() => import('./Pages/Contact/Contact'));
const Testimonials = preloadedLazy(() => import('./Pages/Testimonials/Testimonials'));
const Faq = preloadedLazy(() => import('./Pages/Faq/Faq'));
const Gallery = preloadedLazy(() => import('./Pages/Gallery/Gallery'));
const Home = preloadedLazy(() => import('./Pages/Home/Home/Home'));
const PopularProducts = preloadedLazy(() => import('./Pages/Home/PopularProducts/PopularProducts'));
const BackgroundSlider = preloadedLazy(() => import('./Pages/BackgroundSlider/BackgroundSlider'));
const HomeTestimonials = preloadedLazy(() => import('./Pages/Home/HomeTestimonials/HomeTestimonials'));
const PopupMessage = preloadedLazy(() => import('./Component/PopupMessage/PopupMessage'));
const ScrollUp = preloadedLazy(() => import('./Component/ScrollUp/ScrollUp'));
const PopupSearch = preloadedLazy(() => import('./Component/PopUpSearch/PopupSearch'));
const Theme = preloadedLazy(() => import('./Pages/Theme/Theme'));

function App() {
    const { publicSettings } = usePublicSettings();
    const isEnabled = (slug) => isPageEnabled(slug, publicSettings);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [showPopupSearch, setShowPopupSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const initialLanguage = useMemo(() => getInitialLanguage(), []);

    const debouncedSetSearchTerm = useMemo(
        () => debounce((term) => {
            setSearchTerm(term);
        }, 300),
        []
    );

    const handleSearch = useCallback((query) => {
        debouncedSetSearchTerm(query);
    }, [debouncedSetSearchTerm]);

    const handleClosePopup = () => {
        setShowPopup(false);
        setPopupMessage('');
    };

    const toggleSearchPopup = () => {
        setShowPopupSearch(!showPopupSearch);
    };

    const closePopupSearch = () => {
        setShowPopupSearch(false);
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const pathWithoutLang = stripLangPrefix(location.pathname);
    const hideLayoutPrefixes = ['/checkout', '/order-confirmation', '/auth/callback'];
    const hideLayout = hideLayoutPrefixes.some(prefix => pathWithoutLang.startsWith(prefix));

    return (
        <Suspense fallback={<KineticLoader/>}>
            <ScrollToTop />
            <StoreHead />
            <PageViewBeacon />
            {!hideLayout && (
                <>
                    <Header
                        handleSearch={handleSearch}
                        scrollToTop={scrollToTop}
                        toggleSearchPopup={toggleSearchPopup}
                        navigate={navigate}
                    />
                    <Breadcrumbs />
                </>
            )}

            <Routes>
                <Route path="/" element={<Navigate to={`/${initialLanguage}`} replace />} />
                <Route path="/s/:orgSlug/:storeSlug" element={<StoreScopedRedirect />} />

                {dualStoreRoute('/:lang/product-page', <ProductPage />, 'product-page')}

                {dualStoreRoute('/:lang', (
                    <>
                        <Home />
                        <TrendingCategories navigate={navigate} />
                        <HomeFigure />
                        <PopularProducts />
                        <NewArrival />
                        <HomeTestimonials />
                        <BackgroundSlider />
                    </>
                ), 'home')}

                {dualStoreRoute('/:lang/products', (
                    <Product
                        scrollToTop={scrollToTop}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                ), 'products')}

                {dualStoreRoute('/:lang/product-details/:id', <ProductDetail />, 'product-detail')}
                {isEnabled('about') && dualStoreRoute('/:lang/about', <About />, 'about')}
                {isEnabled('contact') && dualStoreRoute('/:lang/contact', <Contact />, 'contact')}
                {isEnabled('wishlist') && dualStoreRoute('/:lang/wishlist', <Wishlist />, 'wishlist')}
                {isEnabled('subscribe') && dualStoreRoute('/:lang/subscribe', <Subscribe />, 'subscribe')}
                {isEnabled('unsubscribe') && dualStoreRoute('/:lang/unsubscribe', <Unsubscribe />, 'unsubscribe')}
                {isEnabled('testimonials') && dualStoreRoute('/:lang/testimonials', <Testimonials />, 'testimonials')}
                {isEnabled('faq') && dualStoreRoute('/:lang/faq', <Faq />, 'faq')}
                {isEnabled('gallery') && dualStoreRoute('/:lang/gallery', <Gallery />, 'gallery')}
                {dualStoreRoute('/:lang/theme', <Theme />, 'theme')}

                {AuthRoutes(initialLanguage)}

                {CartCheckoutRoutes()}

                {OrderRoutes({
                    isTrackOrderEnabled: isEnabled('track-order'),
                    isMyOrdersEnabled: isEnabled('my-orders'),
                })}

                {/* Redirect legacy routes without language prefix */}
                <Route path="/product-page" element={<Navigate to={`/${initialLanguage}/product-page`} replace />} />
                <Route path="/products" element={<Navigate to={`/${initialLanguage}/products`} replace />} />
                <Route path="/product-details/:id" element={<Navigate to={`/${initialLanguage}/product-details/${location.pathname.split('/').pop()}`} replace />} />
                {isEnabled('about') && <Route path="/about" element={<Navigate to={`/${initialLanguage}/about`} replace />} />}
                {isEnabled('contact') && <Route path="/contact" element={<Navigate to={`/${initialLanguage}/contact`} replace />} />}
                {isEnabled('wishlist') && <Route path="/wishlist" element={<Navigate to={`/${initialLanguage}/wishlist`} replace />} />}
                {isEnabled('subscribe') && <Route path="/subscribe" element={<Navigate to={`/${initialLanguage}/subscribe`} replace />} />}
                {/* Newsletter emails link here without a language prefix; keep the ?email= param. */}
                {isEnabled('unsubscribe') && <Route path="/unsubscribe" element={<Navigate to={`/${initialLanguage}/unsubscribe${location.search}`} replace />} />}
                {isEnabled('testimonials') && <Route path="/testimonials" element={<Navigate to={`/${initialLanguage}/testimonials`} replace />} />}
                {isEnabled('faq') && <Route path="/faq" element={<Navigate to={`/${initialLanguage}/faq${location.hash}`} replace />} />}
                {isEnabled('gallery') && <Route path="/gallery" element={<Navigate to={`/${initialLanguage}/gallery`} replace />} />}
                <Route path="/theme" element={<Navigate to={`/${initialLanguage}/theme`} replace />} />
                <Route path="/login" element={<Navigate to={`/${initialLanguage}/sign-in`} replace />} />
                <Route path="/sign-in" element={<Navigate to={`/${initialLanguage}/sign-in`} replace />} />
                <Route path="/register" element={<Navigate to={`/${initialLanguage}/sign-up`} replace />} />
                <Route path="/sign-up" element={<Navigate to={`/${initialLanguage}/sign-up`} replace />} />
                <Route path="/forgot-password" element={<Navigate to={`/${initialLanguage}/forgot-password`} replace />} />
                <Route path="/reset-password" element={<Navigate to={`/${initialLanguage}/reset-password${location.search}`} replace />} />
                <Route path="/profile" element={<Navigate to={`/${initialLanguage}/profile`} replace />} />
                <Route path="/cart" element={<Navigate to={`/${initialLanguage}/cart`} replace />} />
                <Route path="/checkout/:userId" element={<Navigate to={`/${initialLanguage}/checkout/${location.pathname.split('/').pop()}`} replace />} />
                <Route path="/order-confirmation" element={<Navigate to={`/${initialLanguage}/order-confirmation${location.search}`} replace />} />
                {isEnabled('track-order') && <Route path="/track-order" element={<Navigate to={`/${initialLanguage}/track-order${location.search}`} replace />} />}
                {isEnabled('my-orders') && <Route path="/my-orders" element={<Navigate to={`/${initialLanguage}/my-orders`} replace />} />}
            </Routes>

            {!hideLayout && (
                <>
                    <Footer scrollToTop={scrollToTop} />
                    <ScrollUp />
                </>
            )}

            {showPopup &&
                <PopupMessage
                    message={popupMessage}
                    onClose={handleClosePopup}
                />
            }

            {showPopupSearch && (
                <PopupSearch
                    handleSearch={handleSearch}
                    closePopupSearch={closePopupSearch}
                />
            )}
            <SupportChat />
        </Suspense>
    );
}

export default App;
